import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import useConfirm from '../useConfirm';
import useLoadingModal from '../global/useLoadingModal';
import {
  deleteAutomation,
  invalidateAutomations,
  pauseAutomation,
  resumeAutomation,
} from '../../services/automations';
import { invalidateEmailTemplates } from '../../services/marketing';
import { AutomationActionHandlers } from '../../types/marketing/automations';

type AutomationAction = 'pause' | 'resume' | 'delete';

const AUTOMATION_ACTIONS: Record<
  AutomationAction,
  {
    run: (automationId: string) => Promise<void>;
    confirm: { title: string; message: string; positiveText: string };
    loading: { header: string; message: string };
  }
> = {
  pause: {
    run: pauseAutomation,
    confirm: {
      title: 'Pause automation',
      message:
        'Customers will stop receiving this message until you resume the automation.',
      positiveText: 'Pause',
    },
    loading: {
      header: 'Pausing automation',
      message: 'Please wait while the automation is being paused',
    },
  },
  resume: {
    run: resumeAutomation,
    confirm: {
      title: 'Resume automation',
      message:
        'Customers who hit this trigger from now on will receive this message again.',
      positiveText: 'Resume',
    },
    loading: {
      header: 'Resuming automation',
      message: 'Please wait while the automation is being resumed',
    },
  },
  delete: {
    run: deleteAutomation,
    confirm: {
      title: 'Delete automation',
      message:
        'Are you sure you want to delete this automation? This action cannot be undone.',
      positiveText: 'Delete',
    },
    loading: {
      header: 'Deleting automation',
      message: 'Please wait while the automation is being deleted',
    },
  },
};

function useAutomationActions() {
  const confirm = useConfirm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      action,
      automationId,
    }: {
      action: AutomationAction;
      automationId: string;
    }) => AUTOMATION_ACTIONS[action].run(automationId),
    onMutate: ({ action }) => {
      openLoadingModal(AUTOMATION_ACTIONS[action].loading);
    },
    onSuccess: async (_, { action }) => {
      await invalidateAutomations();
      if (action === 'delete') await invalidateEmailTemplates();
    },
    onError: error => {
      toast.error(error.message);
    },
    onSettled: () => closeLoadingModal(),
  });

  const run = async (action: AutomationAction, automationId: string) => {
    const confirmed = await confirm(AUTOMATION_ACTIONS[action].confirm);
    if (!confirmed) return;
    mutate({ action, automationId });
  };

  const handlers: AutomationActionHandlers = {
    onPauseAutomation: automationId => run('pause', automationId),
    onResumeAutomation: automationId => run('resume', automationId),
    onDeleteAutomation: automationId => run('delete', automationId),
  };

  return { handlers, isPending };
}

export default useAutomationActions;
